import { useState, useCallback } from 'react'
import { SearchCandidatesData } from './use-briefs'

export interface UseSearchFormStateOptions {
  initialRequiredSkills?: string[]
  initialPreferredSkills?: string[]
  initialInstructions?: string
  initialLocation?: string
  initialExperienceLevel?: string
}

export function useSearchFormState(options: UseSearchFormStateOptions = {}) {
  const [requiredSkills, setRequiredSkills] = useState<string[]>(
    options.initialRequiredSkills || []
  )
  const [preferredSkills, setPreferredSkills] = useState<string[]>(
    options.initialPreferredSkills || []
  )
  const [instructions, setInstructions] = useState<string>(
    options.initialInstructions || ''
  )
  const [location, setLocation] = useState<string>(
    options.initialLocation || ''
  )
  const [experienceLevel, setExperienceLevel] = useState<string>(
    options.initialExperienceLevel || 'all'
  )
  const [skillInput, setSkillInput] = useState('')
  const [skillType, setSkillType] = useState<'required' | 'preferred'>('required')

  const handleAddSkill = useCallback(() => {
    if (!skillInput.trim()) return

    if (skillType === 'required') {
      if (!requiredSkills.includes(skillInput.trim())) {
        setRequiredSkills([...requiredSkills, skillInput.trim()])
      }
    } else {
      if (!preferredSkills.includes(skillInput.trim())) {
        setPreferredSkills([...preferredSkills, skillInput.trim()])
      }
    }
    setSkillInput('')
  }, [skillInput, skillType, requiredSkills, preferredSkills])

  const handleRemoveSkill = useCallback(
    (skill: string, type: 'required' | 'preferred') => {
      if (type === 'required') {
        setRequiredSkills(requiredSkills.filter(s => s !== skill))
      } else {
        setPreferredSkills(preferredSkills.filter(s => s !== skill))
      }
    },
    [requiredSkills, preferredSkills]
  )

  const buildSearchData = useCallback((): SearchCandidatesData => {
    return {
      required_skills: requiredSkills.length > 0 ? requiredSkills : undefined,
      preferred_skills: preferredSkills.length > 0 ? preferredSkills : undefined,
      location: location.trim() || undefined,
      experience_level:
        experienceLevel && experienceLevel !== 'all'
          ? (experienceLevel as 'junior' | 'mid' | 'senior' | 'executive')
          : undefined,
      instructions: instructions.trim() || undefined,
      llm_similarity_threshold: 0.7,
      limit: 50,
    }
  }, [requiredSkills, preferredSkills, location, experienceLevel, instructions])

  return {
    // State
    requiredSkills,
    preferredSkills,
    instructions,
    location,
    experienceLevel,
    skillInput,
    skillType,
    // Setters
    setRequiredSkills,
    setPreferredSkills,
    setInstructions,
    setLocation,
    setExperienceLevel,
    setSkillInput,
    setSkillType,
    // Handlers
    handleAddSkill,
    handleRemoveSkill,
    // Builder
    buildSearchData,
  }
}
